import { Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { forOwn } from '@sama/micro-dash';
import { filter } from 'rxjs/operators';
import { MessagingService } from '../../cross-team-shared/messaging/messaging.service';
import { RollbarService } from '../../cross-team-shared/messaging/rollbar.service';
import { CheckboxAnswer } from '../../team-apis/otterdam/answers/checkbox-answer';
import { CustomJS } from './custom-js';
import { isBlank } from './state/output-info';
import { TaskWorkspaceStore } from './state/task-workspace-store';

export interface CustomJsHelper {
  allAreVisibleAndBlank(...outputNames: string[]): boolean;
  allAreBlank(...outputNames: string[]): boolean;
  isBlank(outputName: string): boolean;
  isVisible(outputName: string): boolean;
  getSelectedOptions(checkboxAnswer: CheckboxAnswer | undefined): string[];
  withErrorHandling(fn: VoidFunction): void;
}

@Injectable()
export class CustomJsService {
  constructor(
    private messaging: MessagingService,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private rollbarService: RollbarService,
  ) {}

  compile(js: string): CustomJS | undefined {
    if (!js) {
      return undefined;
    }

    this.taskWorkspaceStore('task')('id')
      .$.pipe(filter(isDefined))
      .subscribe(() => {
        // timeout to let the layout render
        setTimeout(() => {
          this.withErrorHandling(() => {
            const state = this.taskWorkspaceStore.state();
            // fix me if you change this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
            if (state.task) {
              // fix me if you change this code
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
              state.customJs?.onTaskChanged?.(
                this.taskWorkspaceStore.extractAnswersFromOutputInfo(),
              );
            }
          });
        });
      });

    try {
      // using `eval` directly changes its behavior
      // eslint-disable-next-line no-eval -- no workaround
      const evalInGlobalContext = eval;
      const customJsClass = evalInGlobalContext(
        `${js}\n\nCustomJS\n//# sourceURL=CustomJS.js`,
      );
      return new customJsClass(this.makeHelper());
    } catch (error) {
      this.handleError(error as Error);
      return undefined;
    }
  }

  withErrorHandling(fn: VoidFunction) {
    try {
      fn();
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  handleError(error: Error) {
    console.error(error);

    let message = 'There was an error in the custom JavaScript';
    this.messaging.error(message);

    if (error.message) {
      message += `\n${error.message}`;
    }
    error.message = message;
    this.rollbarService.error(error);
  }

  // eslint-disable-next-line max-lines-per-function -- if you need to modify this code, fix this first
  private makeHelper(): CustomJsHelper {
    // Normally it would be nice to make a class for CustomJsHelper, but we
    // want to expose the bare minimum to custom js users. So to avoid giving
    // them access to a full `CustomJsService`, we do it like this.
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- see comment above
    const service = this;
    return {
      allAreVisibleAndBlank(...outputNames: string[]) {
        return outputNames.every(
          (name) => this.isVisible(name) && this.isBlank(name),
        );
      },

      allAreBlank(...outputNames: string[]) {
        return outputNames.every((name) => this.isBlank(name));
      },

      isBlank(outputName: string) {
        const outputInfo = service.getOutputInfo(outputName);
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        return !outputInfo || isBlank(outputInfo);
      },

      isVisible(outputName: string) {
        const outputInfo = service.getOutputInfo(outputName);
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        return outputInfo ? outputInfo.isVisible : false;
      },

      getSelectedOptions(checkboxAnswer: CheckboxAnswer | undefined) {
        const selected: string[] = [];
        forOwn(checkboxAnswer, (val, key) => {
          if (val === '1') {
            selected.push(key);
          }
        });
        return selected;
      },

      withErrorHandling(fn: VoidFunction) {
        service.withErrorHandling(fn);
      },
    };
  }

  private getOutputInfo(outputName: string) {
    return this.taskWorkspaceStore.state().outputInfoByName[outputName];
  }
}
